import BoldText from '@/components/BoldText';
import RegularText from '@/components/RegularText';
import { config } from '@/config';
import { hexToRgbaObject, openErrorToast, openToast, rgbaToHex } from '@/helpers';
import { StyledClickAble } from '@/pages/GeneralSetting/styled';
import InputColorPicker from '@/pages/WidgetAppearance/component/common/InputColorPicker';
import { useUploadBrandFileMutation, useVerifyEmailMutation } from '@/redux/query';
import { emailSettingSelector, handleSaveEmailSettings } from '@/redux/slice/email.slice';
import { handleSetSaveBar } from '@/redux/slice/savebar.slice';
import {
  Badge,
  BlockStack,
  Box,
  Button,
  Card,
  DropZone,
  Icon,
  InlineGrid,
  InlineStack,
  Spinner,
  TextField,
  Thumbnail,
} from '@shopify/polaris';
import { NoteIcon, XIcon } from '@shopify/polaris-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

const BrandSetting = () => {
  const { t } = useTranslation('general_settings');
  const dispatch = useDispatch();

  const emailSettings = useSelector(emailSettingSelector);
  const [uploadBrandFile, { isLoading: isLoadingUploadBrandFile }] = useUploadBrandFileMutation();
  const [verifyEmail, { isLoading: isLoadingVerifyEmail }] = useVerifyEmailMutation();

  const [file, setFile] = useState<File>();
  const [cooldown, setCooldown] = useState(0);

  const validImageTypes = useMemo(() => ['image/gif', 'image/jpeg', 'image/png'], []);

  const handleDropZoneDrop = useCallback(
    (_dropFiles: File[], acceptedFiles: File[], _rejectedFiles: File[]) => {
      const droppedFile = acceptedFiles[0];
      if (droppedFile && validImageTypes.includes(droppedFile.type)) {
        const originalName = droppedFile.name;
        const extension = originalName.substring(originalName.lastIndexOf('.') + 1);
        const newFile = new File([droppedFile], `brand.${extension}`, { type: droppedFile.type });
        setFile(newFile);
        uploadBrandFile({
          file: newFile,
          shop: config.shop,
        })
          .unwrap()
          .then((res) => {
            dispatch(handleSaveEmailSettings({ logo: res.data.url }));
          })
          .catch(() => {
            openErrorToast(dispatch, 'Failed to upload file. Please try again.');
          });
      } else {
        openErrorToast(dispatch, 'Invalid file type. Please upload a .gif, .jpg, or .png file.');
      }
    },
    [dispatch, uploadBrandFile, validImageTypes],
  );

  const loadingState = isLoadingUploadBrandFile && (
    <Box padding={'300'}>
      <BlockStack gap="100" inlineAlign="center" align="center">
        <Spinner size="small" />
        <RegularText tone="subdued">Uploading...</RegularText>
      </BlockStack>
    </Box>
  );

  const fileUpload = !file && !isLoadingUploadBrandFile && <DropZone.FileUpload actionHint="Accepts .gif, .jpg, and .png" />;

  const uploadedFile = file && !isLoadingUploadBrandFile && (
    <Box padding={'300'}>
      <InlineStack blockAlign="center" align="space-between">
        <InlineStack gap="300" blockAlign="center">
          <Thumbnail alt={file.name} source={validImageTypes.includes(file.type) ? window.URL.createObjectURL(file) : NoteIcon} />
          <div>{file.name} </div>
        </InlineStack>
        <Box>
          <StyledClickAble
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              setFile(undefined);
              dispatch(handleSaveEmailSettings({ logo: '' }));
            }}
          >
            <Icon source={XIcon} />
          </StyledClickAble>
        </Box>
      </InlineStack>
    </Box>
  );

  const handleVerifyEmail = useCallback(() => {
    verifyEmail({
      shop: config.shop,
      sender: emailSettings.sender,
    })
      .unwrap()
      .then(() => {
        openToast(dispatch, 'Resend email successfully');
        setCooldown(60);
      })
      .catch(() => openErrorToast(dispatch, 'Resend email failed'));
  }, [dispatch, emailSettings.sender, verifyEmail]);

  useEffect(() => {
    if (!emailSettings.logo) {
      setFile(undefined);
    } else {
      const fetchImageAsFile = async (url: string) => {
        try {
          dispatch(handleSetSaveBar({ isUploadingFile: true }));
          const response = await fetch(url);
          const blob = await response.blob();

          const contentType = blob.type || 'application/octet-stream';

          let extension = '';
          if (contentType.startsWith('image/')) {
            extension = '.' + contentType.split('/')[1];
          } else {
            const urlParts = url.split('/');
            const originalFilename = urlParts[urlParts.length - 1];
            const match = originalFilename.match(/\.(\w{2,5})$/);
            if (match) {
              extension = '.' + match[1];
            }
          }

          const filename = `brand${extension || ''}`;
          const newFile = new File([blob], filename, { type: contentType });

          dispatch(handleSetSaveBar({ isUploadingFile: false }));
          setFile(newFile);
        } catch (error) {
          console.error('Error fetching the image:', error);
        }
      };

      fetchImageAsFile(emailSettings.logo);
    }
  }, [dispatch, emailSettings.logo]);

  useEffect(() => {
    if (cooldown <= 0) return;

    const timer = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldown]);

  return (
    <div id="brand-settings">
      <Card>
        <BlockStack gap={'300'}>
          <Box>
            <BoldText>{t('sections.brandSetting.title')}</BoldText>
            <RegularText>{t('sections.brandSetting.helpText')}</RegularText>
          </Box>
          <InlineGrid columns={{ xs: 1, md: 2 }} gap={'800'}>
            <BlockStack gap={'100'}>
              <TextField
                type="email"
                label={
                  <InlineStack gap={'200'}>
                    <RegularText>{t('sections.brandSetting.emailLabel')}</RegularText>
                    {emailSettings.isVerifyEmail && !emailSettings.fieldsChange?.hasOwnProperty('sender') ? (
                      <Badge progress="complete" tone="success">
                        {t('sections.brandSetting.verified')}
                      </Badge>
                    ) : (
                      <Badge progress="partiallyComplete" tone="warning">
                        {t('sections.brandSetting.unverified')}
                      </Badge>
                    )}
                  </InlineStack>
                }
                value={emailSettings.sender}
                onChange={(value) => {
                  dispatch(handleSaveEmailSettings({ sender: value, isErrorEmail: false }));
                }}
                autoComplete="off"
                error={
                  emailSettings.isErrorEmail
                    ? emailSettings.sender.trim() === ''
                      ? t('sections.brandSetting.errorEmptyEmail')
                      : t('sections.brandSetting.errorInvalidEmail')
                    : undefined
                }
              />
              <InlineStack gap={'100'}>
                <RegularText>{t('sections.brandSetting.resendMailQuestion')}</RegularText>
                {cooldown > 0 ? (
                  <Badge>{`${cooldown}s`}</Badge>
                ) : (
                  <Button variant="plain" disabled={isLoadingVerifyEmail || cooldown > 0} onClick={handleVerifyEmail}>
                    {t('sections.brandSetting.resendButton')}
                  </Button>
                )}
              </InlineStack>
            </BlockStack>
            <InputColorPicker
              label={t('sections.brandSetting.colourLabel')}
              colorKey={'brand-color'}
              colorValue={hexToRgbaObject(emailSettings.accentColour)}
              onChange={(value) => dispatch(handleSaveEmailSettings({ accentColour: rgbaToHex(value) }))}
            />
          </InlineGrid>
          <DropZone allowMultiple={false} onDrop={handleDropZoneDrop} variableHeight>
            {uploadedFile}
            {loadingState}
            {fileUpload}
          </DropZone>
        </BlockStack>
      </Card>
    </div>
  );
};

export default BrandSetting;
